import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Grid,
    Paper,
    TextField,
    Button,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    IconButton,
    InputAdornment,
    Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
    getUserDetails,
    resetAllProgress,
    updatePassword,
    updateUserDetails,
    cancelSubscription,
    // Aliasing the backend function for clarity
    getSubscriptionStatus,
    // NEW: Placeholder for API to get Stripe Portal URL
    createCustomerPortalSession,
} from "../../../../api/auth";
import { useSelector } from "react-redux";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";

const DashboardSettings = () => {
    const userId = useSelector((state) => state.auth.userId);

    const [userData, setUserData] = useState({
        username: "",
        userId: "",
        email: "",
        firstName: "",
        lastName: "",
        universityName: "",
        classYear: "",
        graduationYear: "",
        isVerified: false,
        subscriptionId: "", // Used for making cancellation API call
    });

    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
    });

    // UPDATED STATE: Added payment method fields (last4, brand)
    const [subscriptionStatus, setSubscriptionStatus] = useState({
        isActive: false,       // Current active state (boolean)
        nextBillingDate: null, // Date object or null
        cancellationDate: null, // Date object or null (end of access)
        rawStatus: null,       // Raw Stripe status string
        subscriptionId: null, // Ensure ID is captured from status call
        last4: null,           // ⭐️ NEW: Last 4 digits of card ⭐️
        brand: null,           // ⭐️ NEW: Card brand (Visa, Mastercard, etc.) ⭐️
    });

    // States for toggling password visibility
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    // Helper function to format date
    const formatDate = (date) => (date ? date.toLocaleDateString() : 'N/A');

    const handleConfirmCancellation = async () => {
        // Check if the subscription ID is present
        if (!userData.subscriptionId) {
            alert("No subscription found to cancel.");
            handleCloseDialog();
            return;
        }

        // Check if it's already past its active period
        if (!subscriptionStatus.isActive) {
            alert(`Your subscription is already ${subscriptionStatus.rawStatus}. No further cancellation is possible.`);
            handleCloseDialog();
            return;
        }

        // Check if cancellation is already scheduled
        if (subscriptionStatus.cancellationDate && subscriptionStatus.cancellationDate.getTime() > Date.now()) {
            alert(`Your subscription is already scheduled to cancel on ${formatDate(subscriptionStatus.cancellationDate)}. No action is needed.`);
            handleCloseDialog();
            return;
        }


        try {
            // Pass the ID stored in the userData state to the cancel API
            const response = await cancelSubscription(userData.subscriptionId);

            console.log("Subscription cancelled:", response);
            alert("Subscription cancelled successfully. Your subscription will remain active until the end of the current billing cycle.");

            handleCloseDialog();

            // ⭐️ Force page refresh after successful cancellation
            window.location.reload();

        } catch (error) {
            console.error("Error cancelling subscription:", error);
            const errorMsg = error.response?.data?.error || "Failed to cancel subscription due to a server error.";
            alert(errorMsg);
        }
    };

    // Handler for changing payment method
    const handleChangePaymentMethod = async () => {
        try {
            // 1. Call the API
            const response = await createCustomerPortalSession(userId);

            // 2. The portal URL is inside response.data.portalUrl
            const portalUrl = response.data.portalUrl;

            if (portalUrl) {
                // 3. Initiate Redirection
                window.location.href = portalUrl;
            } else {
                alert("Could not generate the payment portal link (URL field missing). Please try again.");
            }
        } catch (error) {
            console.error("Error creating customer portal session:", error);
            alert("Failed to access payment settings. Please try again later.");
        }
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handlePasswordUpdate = async () => {
        // ... (handlePasswordUpdate logic remains the same)
        if (
            !passwords.currentPassword ||
            !passwords.newPassword ||
            !passwords.confirmNewPassword
        ) {
            alert("Please fill in all the password fields.");
            return;
        }

        if (passwords.newPassword !== passwords.confirmNewPassword) {
            alert("New passwords do not match!");
            return;
        }

        try {
            const response = await updatePassword({
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword,
                confirmNewPassword: passwords.confirmNewPassword,
            });
            alert("Password updated successfully");
        } catch (error) {
            if (error.response && error.response.status === 400) {
                const errorMsg = error.response.data.errors[0].msg;
                if (errorMsg === "Current password is incorrect.") {
                    alert("The current password you entered is incorrect.");
                } else if (errorMsg === "Passwords do not match.") {
                    alert("The new passwords do not match.");
                } else {
                    alert("Failed to update password. Please try again.");
                }
            } else {
                console.error("Error updating password:", error);
                alert("Failed to update password. Please try again.");
            }
        }
    };

    // CONSOLIDATED FUNCTION to fetch ALL user data
    const fetchUserDetails = async () => {
        try {
            const userResponse = await getUserDetails();
            const subStatusResponse = await getSubscriptionStatus({ userId }); // Ensure userId is passed if backend requires it

            // ⭐️ CONSOLE LOGGING FOR DEBUGGING ⭐️
            console.log("--- New Subscription Status from Backend ---");
            console.log("Full Response:", subStatusResponse);
            console.log("Last 4 Digits:", subStatusResponse.last4);
            console.log("Card Brand:", subStatusResponse.brand);
            console.log("------------------------------------------");

            if (userResponse.data) {
                setUserData((currentUserData) => ({
                    ...currentUserData,
                    username: userResponse.data.username,
                    email: userResponse.data.email,
                    firstName: userResponse.data.firstName,
                    lastName: userResponse.data.lastName,
                    universityName: userResponse.data.universityName,
                    classYear: userResponse.data.classYear,
                    graduationYear: userResponse.data.graduationYear,
                    isVerified: userResponse.data.isVerified,
                    subscriptionId: subStatusResponse.subscriptionId || "",
                }));
            }

            // 4. Update subscription status state
            setSubscriptionStatus({
                isActive: subStatusResponse.isActive,
                nextBillingDate: subStatusResponse.nextBillingDate ? new Date(subStatusResponse.nextBillingDate) : null,
                cancellationDate: subStatusResponse.cancellationDate ? new Date(subStatusResponse.cancellationDate) : null,
                rawStatus: subStatusResponse.rawStatus,
                subscriptionId: subStatusResponse.subscriptionId,
                last4: subStatusResponse.last4, // ⭐️ NEW ⭐️
                brand: subStatusResponse.brand, // ⭐️ NEW ⭐️
            });

        } catch (error) {
            console.error("Error fetching user or subscription details:", error);
            setSubscriptionStatus({
                isActive: false,
                nextBillingDate: null,
                cancellationDate: null,
                rawStatus: 'error',
                subscriptionId: null,
                last4: null,
                brand: null,
            });
        }
    }


    useEffect(() => {
        fetchUserDetails();
    }, [userId]);

    async function handleResetProgress() {
        try {
            const response = await resetAllProgress(userId);
            if (response.data.success) {
                console.log("Progress reset successfully");

                alert("Your progress has been reset successfully!");
            } else {
                console.error("Error resetting progress:", response.data.error);
                alert(
                    "There was an error resetting your progress. Please try again later."
                );
            }
        } catch (error) {
            console.error("Error calling resetAllProgress API:", error);
            alert(
                "There was an error resetting your progress. Please try again later."
            );
        }
    }

    const classYearOptions = [
        { value: "Year 1", label: "Year 1" },
        { value: "Year 2", label: "Year 2" },
        { value: "Year 3", label: "Year 3" },
        { value: "Year 4", label: "Year 4" },
        { value: "Year 5", label: "Year 5" },
    ];

    const currentYear = new Date().getFullYear();
    const yearsRange = Array.from({ length: 10 }, (_, i) => currentYear + i);


    const getPaymentMethodDisplay = () => {
        const { brand, last4 } = subscriptionStatus;
        if (brand && last4) {
            // Capitalize first letter of brand for nice display
            const formattedBrand = brand.charAt(0).toUpperCase() + brand.slice(1);
            return ` (Billing: ${formattedBrand} ending in ****${last4})`;
        }
        return '';
    };

    // Determine the content for the Subscription Options section
    const SubscriptionDetails = () => {
        const paymentDisplay = getPaymentMethodDisplay();

        if (!userData.subscriptionId) {
            return (
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                    No active subscription found.
                </Typography>
            );
        }

        if (subscriptionStatus.cancellationDate) {
            if (subscriptionStatus.isActive) {
                // Case 1: Active subscription with a future cancellation scheduled
                return (
                    <>
                        <Typography color="warning" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Cancellation Scheduled {paymentDisplay}
                        </Typography>
                        <Typography sx={{ mb: 2 }}>
                            Your subscription will remain active until **{formatDate(subscriptionStatus.cancellationDate)}**.
                        </Typography>
                    </>
                );
            } else {
                // Case 2: Inactive subscription that has already been cancelled/ended
                return (
                    <>
                        <Typography color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Subscription Ended
                        </Typography>
                        <Typography sx={{ mb: 2 }}>
                            Your subscription ended on **{formatDate(subscriptionStatus.cancellationDate)}**.
                        </Typography>
                    </>
                );
            }
        }

        if (subscriptionStatus.isActive && subscriptionStatus.nextBillingDate) {
            // Case 3: Truly active subscription 
            return (
                <>
                    <Typography color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Active Subscription {paymentDisplay}
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Your next payment is scheduled for **{formatDate(subscriptionStatus.nextBillingDate)}**.
                    </Typography>
                </>
            );
        }

        // Fallback for any other non-active, non-cancelled-date status (e.g., 'past_due', 'unpaid')
        return (
            <Typography color="textSecondary" sx={{ mb: 2 }}>
                Subscription status: **{subscriptionStatus.rawStatus || 'Inactive'}**.
            </Typography>
        );
    };


    return (
        <Box
            component="main"
            sx={{
                backgroundColor: "rgb(249, 249, 249)",
                flexGrow: 1,
                height: "100vh",
                overflow: "auto",
                p: 2,
            }}
        >
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <div>
                    <h1 style={{ margin: "20px 0" }}>Dashboard Settings</h1>
                </div>
                <Grid container spacing={2}>

                    {/* Edit Account Details */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 240,
                                boxShadow: "0 6px 20px rgba(56, 125, 255, 0.05)",
                                border: "1px solid rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <div>
                                <h2 style={{ marginBottom: "20px" }}>Edit Account Details</h2>
                            </div>
                            <Grid container spacing={2}>
                                {/* ... (Input fields) ... */}
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        variant="filled"
                                        value={userData.firstName}
                                        onChange={(e) =>
                                            setUserData({ ...userData, firstName: e.target.value })
                                        }
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        variant="filled"
                                        value={userData.lastName}
                                        onChange={(e) =>
                                            setUserData({ ...userData, lastName: e.target.value })
                                        }
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Username"
                                        variant="filled"
                                        value={userData.username}
                                        disabled
                                        onChange={(e) =>
                                            setUserData({ ...userData, username: e.target.value })
                                        }
                                        margin="normal"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                "& .Mui-disabled": { color: "grey" },
                                                "&:before": { borderBottom: "1px solid black" },
                                                "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid black" },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        variant="filled"
                                        value={userData.email}
                                        disabled
                                        onChange={(e) =>
                                            setUserData({ ...userData, email: e.target.value })
                                        }
                                        margin="normal"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                "& .Mui-disabled": { color: "grey" },
                                                "&:before": { borderBottom: "1px solid black" },
                                                "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid black" },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="University"
                                        variant="filled"
                                        value={userData.universityName}
                                        disabled
                                        onChange={(e) =>
                                            setUserData({
                                                ...userData,
                                                universityName: e.target.value,
                                            })
                                        }
                                        margin="normal"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                "& .Mui-disabled": { color: "grey" },
                                                "&:before": { borderBottom: "1px solid black" },
                                                "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid black" },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="isVerfied"
                                        variant="filled"
                                        value={userData.isVerified ? "Yes" : "No"}
                                        disabled
                                        onChange={(e) =>
                                            setUserData({ ...userData, isVerified: e.target.value })
                                        }
                                        margin="normal"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                "& .Mui-disabled": { color: "grey" },
                                                "&:before": { borderBottom: "1px solid black" },
                                                "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid black" },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth margin="normal" variant="filled">
                                        <InputLabel>Class Year</InputLabel>
                                        <Select
                                            value={userData.classYear || ""}
                                            onChange={(e) =>
                                                setUserData({ ...userData, classYear: e.target.value })
                                            }
                                            label="Class Year"
                                        >
                                            {classYearOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl fullWidth margin="normal" variant="filled">
                                        <InputLabel>Graduation Year</InputLabel>
                                        <Select
                                            value={userData.graduationYear || ""}
                                            onChange={(e) =>
                                                setUserData({
                                                    ...userData,
                                                    graduationYear: e.target.value,
                                                })
                                            }
                                            label="Graduation Year"
                                        >
                                            {yearsRange.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Button
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={async () => {
                                    try {
                                        const response = await updateUserDetails({
                                            first_name: userData.firstName,
                                            last_name: userData.lastName,
                                            user_name: userData.username,
                                            class_year: userData.classYear,
                                            graduation_year: parseInt(userData.graduationYear),
                                            user_id: userId,
                                        });
                                        alert(response.data.message);
                                    } catch (error) {
                                        console.error("Error updating user details:", error);
                                        alert(error.response?.data?.error || "Failed to update user details.");
                                    }
                                }}
                            >
                                Update Profile
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Update Password */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 240,
                                boxShadow: "0 6px 20px rgba(56, 125, 255, 0.05)",
                                border: "1px solid rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <h2 style={{ marginBottom: "20px" }}>Update Password</h2>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Current Password"
                                        type={showCurrentPassword ? "text" : "password"}
                                        name="currentPassword"
                                        variant="filled"
                                        value={passwords.currentPassword}
                                        onChange={handlePasswordChange}
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            setShowCurrentPassword(!showCurrentPassword)
                                                        }
                                                        edge="end"
                                                    >
                                                        {showCurrentPassword ? (
                                                            <VisibilityOff />
                                                        ) : (
                                                            <Visibility />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="New Password"
                                        type={showNewPassword ? "text" : "password"}
                                        name="newPassword"
                                        variant="filled"
                                        value={passwords.newPassword}
                                        onChange={handlePasswordChange}
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                                        edge="end"
                                                    >
                                                        {showNewPassword ? (
                                                            <VisibilityOff />
                                                        ) : (
                                                            <Visibility />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Confirm New Password"
                                        type={showConfirmNewPassword ? "text" : "password"}
                                        name="confirmNewPassword"
                                        variant="filled"
                                        value={passwords.confirmNewPassword}
                                        onChange={handlePasswordChange}
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            setShowConfirmNewPassword(!showConfirmNewPassword)
                                                        }
                                                        edge="end"
                                                    >
                                                        {showConfirmNewPassword ? (
                                                            <VisibilityOff />
                                                        ) : (
                                                            <Visibility />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Button
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={handlePasswordUpdate}
                            >
                                Update Password
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Progression */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                boxShadow: "0 6px 20px rgba(56, 125, 255, 0.05)",
                                border: "1px solid rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <div>
                                <h2 style={{ marginBottom: "20px" }}>Progression</h2>
                            </div>

                            <div className="reset-button-container">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleResetProgress}
                                    sx={{
                                        backgroundColor: "black",
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "gray",
                                        },
                                    }}
                                >
                                    Reset All Progress
                                </Button>
                            </div>
                        </Paper>
                    </Grid>

                    {/* Subscription Options (Updated) */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 160,
                                boxShadow: "0 6px 20px rgba(56, 125, 255, 0.05)",
                                border: "1px solid rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <h2>Subscription Options</h2>

                            {/* Display subscription status */}
                            <SubscriptionDetails />

                            <div
                                className="subscription-buttons-container"
                                style={{ paddingTop: "15px", display: 'flex', gap: '10px' }} // Added flex container for buttons
                            >
                                {/* NEW: Change Payment Method Button */}
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleChangePaymentMethod}
                                    // Disable if no subscription ID exists (no customer to update)
                                    disabled={!userData.subscriptionId}
                                    sx={{ color: 'black', borderColor: 'black', '&:hover': { borderColor: 'gray' } }}
                                >
                                    Change Payment Method
                                </Button>

                                {/* Cancel Subscription Button */}
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleOpenDialog}
                                    // Disable the button if no ID exists OR if the subscription is not currently active
                                    disabled={!userData.subscriptionId || !subscriptionStatus.isActive}
                                >
                                    {
                                        subscriptionStatus.cancellationDate && subscriptionStatus.isActive
                                            ? 'Cancellation Scheduled'
                                            : (subscriptionStatus.isActive ? 'Cancel Subscription' : 'Not Active')
                                    }
                                </Button>
                            </div>
                        </Paper>
                    </Grid>

                    {/* Dialog (Updated content) */}
                    <Dialog
                        open={openDialog}
                        onClose={handleCloseDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            {"Confirm Subscription Cancellation"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Are you sure you want to cancel your subscription?
                                Your access will remain until the end of the current billing period
                                which is **{formatDate(subscriptionStatus.nextBillingDate)}**.
                                This action cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog} color="primary">
                                Keep Subscription
                            </Button>
                            <Button
                                onClick={handleConfirmCancellation}
                                color="error" // Use error color for destructive action
                                autoFocus
                            >
                                Confirm Cancellation
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Container>
        </Box>
    );
};

export default DashboardSettings;